import type { MissionContext } from '@gmao/shared';
import type { ComputedRef, Plugin } from 'vue';
import type { Router } from 'vue-router';
import { missionContextState as ctx } from '@gmao/shared';
import { idb, needSync, serverNow, socketIo } from '@gmao/sync';
import { checkUpdates } from '@operations/features/missions/updater';
import { useIntervalFn } from '@vueuse/core';
import { isAfter, isBefore } from 'date-fns';
import { computed, inject, watch } from 'vue';

interface MissionContextOptions {
  router: Router;
}

type MissionContextReadonly = Required<{
  [K in keyof MissionContext]: ComputedRef<Exclude<MissionContext[K], undefined>>;
}> & {
  isLocked: ComputedRef<boolean>;
  setMachineId(id: number): void;
  setHasUpdates(value: boolean): void;
  setHasEventsUpdates(value: boolean): void;
  checkUpdates(): Promise<void>;
};

const CKECK_UPDATE_INTERVAL = 60_000;

export const missionContext: Plugin = {
  install(app, { router }: MissionContextOptions) {
    async function _checkStructuresUpdates() {
      const { structure, nom, amos } = await checkUpdates(ctx.mission!.id);
      if (structure || nom || amos) {
        ctx.hasUpdates = true;
      }
    }

    async function _checkEventsUpdates() {
      ctx.hasEventsUpdates = await needSync(ctx.mission!.id);
    }

    const { pause: pauseCheckUpdates, resume: resumeCheckUpdates } = useIntervalFn(
      checkAllUpdates,
      CKECK_UPDATE_INTERVAL,
    );

    async function checkAllUpdates() {
      if (!ctx.mission || !socketIo.connected)
        return;
      try {
        pauseCheckUpdates();
        await _checkStructuresUpdates();
        await _checkEventsUpdates();
      }
      finally {
        resumeCheckUpdates();
      }
    }
    pauseCheckUpdates();

    async function getMission(id: string) {
      ctx.isLoading = true;

      try {
        ctx.mission = await idb.missions.get(id);
        resumeCheckUpdates();

        if (
          !ctx.currentMachineId
          || !ctx.mission?.machines.find(m => m.id === ctx.currentMachineId)
        ) {
          ctx.currentMachineId = ctx.mission?.machines[0].id || 0;
        }
      }
      finally {
        ctx.isLoading = false;
      }
    }

    watch(
      () => router.currentRoute.value.params.missionId,
      (missionId) => {
        if (!missionId)
          pauseCheckUpdates();
      },
      { immediate: true },
    );

    router.beforeEach(async (to) => {
      if (to.params.missionId && to.params.missionId !== ctx.mission?.id) {
        await getMission(to.params.missionId as string);
      }
    });

    app.provide('missionContext', {
      mission: computed(() => ctx.mission!),
      currentMachineId: computed(() => ctx.currentMachineId!),
      isLoading: computed(() => ctx.isLoading),
      hasUpdates: computed(() => ctx.hasUpdates),
      setHasUpdates(value: boolean) {
        ctx.hasUpdates = value;
      },
      hasEventsUpdates: computed(() => ctx.hasEventsUpdates),
      setHasEventsUpdates(value: boolean) {
        ctx.hasEventsUpdates = value;
      },
      isLocked: computed(() => {
        if (!ctx.mission)
          return true;

        const now = serverNow();
        if (isAfter(now, new Date(ctx.mission.startDate))) {
          if (!ctx.mission.endDate)
            return false;
          else if (ctx.mission.endDate && isBefore(now, new Date(ctx.mission.endDate)))
            return false;
        }

        return true;
      }),
      setMachineId(id) {
        ctx.currentMachineId = id;
      },
      checkUpdates: checkAllUpdates,
    } satisfies MissionContextReadonly);
  },
};

export function useMissionContext(): MissionContextReadonly {
  return inject('missionContext')!;
}
